"use client";
import { useCallback, useEffect, useState } from "react";
import { useScopedI18n } from "@/messages/client";
import Title from "@/components/ui/title";
import { DirectusImage } from "@/components/DirectusImage";
import RouterLink from "@/components/ui/router-link";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import useLangData from "@/hooks/useLangData";

export default function CaseProductClient(props: {
  data?: {
    slug?: string;
    card_cover?: string;
    name?: string;
    summary?: string;
    lang_data: {
      lang: string;
      card_cover?: string;
      name?: string;
      summary?: string;
    }[];
  }[];
  lang: string;
}) {
  const t = useScopedI18n("应用");

  const getLangData = useLangData<{
    lang: string;
    card_cover?: string;
    name?: string;
    summary?: string;
  }>();

  const [current, setCurrent] = useState(0);
  const [mainApi, setMainApi] = useState<CarouselApi | null>(null);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const onSelect = useCallback(() => {
    if (!mainApi) {
      return;
    }
    setCurrent(mainApi.selectedScrollSnap() || 0);
  }, [mainApi]);

  useEffect(() => {
    if (!mainApi) {
      return;
    }
    setScrollSnaps(mainApi.scrollSnapList());

    mainApi.on("reInit", () => {
      setScrollSnaps(mainApi.scrollSnapList());
    });
  }, [mainApi]);

  useEffect(() => {
    if (!mainApi) {
      return;
    }
    onSelect();
    mainApi.on("select", onSelect).on("reInit", onSelect);
  }, [mainApi, onSelect]);

  return (
    <div className="w-full max-md:-ml-6 max-md:w-[100vw]">
      <Title title={t("相关产品推荐")} underline={false} />
      <div className="mt-5 w-full md:mt-12">
        <Carousel
          className="w-full"
          opts={{
            loop: true,
            breakpoints: {
              "(min-width:678px)": {
                align: "start",
              },
            },
          }}
          setApi={setMainApi}>
          <CarouselContent className="-ml-2 md:m-0">
            {props.data?.map((el, index) => (
              <CarouselItem
                className="basis-[68vw] max-md:pl-2 md:basis-1/2 md:px-2 md:py-1 lg:basis-1/3"
                key={index}>
                <div
                  className={cn("h-[332px] md:h-[432px] lg:h-[414px]", {
                    "max-md:opacity-50": current !== index,
                  })}>
                  <div className="group h-[310px] rounded-lg md:hover-outline-shadow md:h-[406px]">
                    <div className="flex h-[144px] w-full items-center justify-center overflow-hidden rounded-lg md:h-[214px]">
                      <DirectusImage
                        id={el.card_cover || ""}
                        alt={el.name || ""}
                        width={256}
                        height={144}
                        zoomInGroup
                        className="h-full w-full object-scale-down"
                      />
                    </div>
                    <div className="mt-3 px-5 md:mt-4 md:px-7">
                      <p className="overflow-hidden text-ellipsis text-lg font-bold md:text-2xl">
                        {getLangData("name", el.name || "", el.lang_data)}
                      </p>
                      <p className="mt-0.5 line-clamp-3 h-14 text-ellipsis text-xs leading-relaxed text-default md:mt-1.5">
                        {getLangData("summary", el.summary || "", el.lang_data)}
                      </p>
                      <RouterLink
                        href={el.slug ? `/product/${el.slug}` : undefined}
                        className="mt-4 w-full md:mt-3">
                        <Button className="w-full rounded-md">
                          {t("前往查看产品详情")}
                        </Button>
                      </RouterLink>
                    </div>
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="mt-4 flex w-full items-center justify-center space-x-2 md:hidden">
            {scrollSnaps.map((_, index) => (
              <div
                key={index}
                className={cn(
                  "h-1.5 w-1.5 rounded-full bg-[#CCCCCC] transition-all duration-300 ease-in-out",
                  {
                    "w-4 bg-[#2C2C2C]": current === index,
                  }
                )}></div>
            ))}
          </div>
        </Carousel>
      </div>
    </div>
  );
}
